import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { authFetch } from '../utils/apiClient';
import { resolvePhotoUrl } from '../utils/imageUtils';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

export default function MarketCheckout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToCart, cart } = useCart();
  const [product, setProduct] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addedQuantity, setAddedQuantity] = useState(0);
  const sku = searchParams.get('sku');

  useEffect(() => {
    if (!sku) {
      setError('No product SKU provided');
      setLoading(false);
      return;
    }

    const fetchProduct = async () => {
      try {
        // Fetch product by SKU
        const productsResponse = await authFetch(`${API_URL}/api/public/products`);
        if (!productsResponse.ok) throw new Error('Failed to fetch products');
        const productsData = await productsResponse.json();
        const foundProduct = productsData.products.find(p => p.sku === sku);
        
        if (!foundProduct) {
          setError(`Product not found: ${sku}`);
          setLoading(false);
          return;
        }

        setProduct(foundProduct);
        setAddedQuantity(0);

        // Fetch inventory for just this product across all locations and sum it up
        const inventoryResponse = await authFetch(`${API_URL}/api/inventory/${foundProduct.id}`);
        if (inventoryResponse.ok) {
          const inventoryData = await inventoryResponse.json();

          const totalQuantity = (inventoryData.inventory || [])
            .reduce((sum, inv) => sum + (inv.quantity || 0), 0);

          setInventory({ productId: foundProduct.id, quantity: totalQuantity });
        } else {
          console.error('Failed to fetch inventory:', inventoryResponse.status);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Failed to load product');
        setLoading(false);
      }
    };

    fetchProduct();
  }, [sku]);

  const handleAddToCart = () => {
    if (product && quantity > 0) {
      addToCart(product, quantity);
      setAddedQuantity(quantity);
      // Show success feedback
      const button = document.getElementById('add-to-cart-btn');
      if (button) {
        button.textContent = '✓ Added!';
        button.classList.add('bg-green-600');
        setTimeout(() => {
          button.textContent = '🛒 Add to Cart';
          button.classList.remove('bg-green-600');
        }, 1500);
      }
    }
  };

  const handleCheckout = () => {
    if (cart.length > 0 || quantity > 0) {
      // Only add the current selection if it hasn't already been added to the
      // cart (e.g. via "Add to Cart"). Without this check, tapping Checkout
      // re-adds the same quantity every time, including after a failed
      // checkout-init navigation that sends the user back to this page.
      if (quantity > 0 && product && quantity > addedQuantity) {
        addToCart(product, quantity - addedQuantity);
        setAddedQuantity(quantity);
      }
      navigate('/checkout');
    }
  };

  const handlePayNow = async () => {
    if (product && inStock && quantity > 0) {
      try {
        // Create Stripe Checkout Session with the selected quantity
        const response = await authFetch(`${API_URL}/api/checkout/create-checkout-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            productId: product.id,
            quantity: quantity
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to create checkout session');
        }

        const data = await response.json();
        
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } catch (error) {
        console.error('Checkout error:', error);
        alert(`Error: ${error.message}`);
      }
    }
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const stock = inventory?.quantity || 0;
  const inStock = stock > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sage-50 to-cream-200 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-sage-700 mx-auto mb-4" />
          <p className="text-ink-soft text-lg">Loading product...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sage-50 to-cream-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="font-serif text-2xl font-bold text-ink mb-2">Product Not Found</h1>
          <p className="text-ink-soft mb-6">{error || 'Unable to load product details'}</p>
          <button
            onClick={() => navigate('/shop')}
            className="w-full bg-sage-700 text-white py-3 rounded-xl font-semibold hover:bg-sage-800 transition-colors"
          >
            Browse All Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-cream-200">
      {/* Header with Cart Summary */}
      <div className="bg-white shadow-md sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/shop')}
            className="text-sage-700 font-semibold flex items-center gap-2"
          >
            ← Back
          </button>
          {cartItemCount > 0 && (
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-xs text-ink-muted">Cart</p>
                <p className="font-bold text-sage-700">{cartItemCount} items · ${cartTotal.toFixed(2)}</p>
              </div>
              <button
                onClick={handleCheckout}
                className="bg-sage-700 text-white px-4 py-2 rounded-lg font-semibold hover:bg-sage-800 transition-colors"
              >
                Checkout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto p-4 pb-32">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Product Image */}
          <div className="aspect-square bg-cream-200 flex items-center justify-center overflow-hidden">
            {product.photoUrl ? (
              <img
                src={resolvePhotoUrl(product.photoUrl)}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-ink-muted text-6xl">📷</div>
            )}
          </div>

          {/* Product Details */}
          <div className="p-6">
            {/* Stock Status Badge */}
            <div className="mb-4">
              {inStock ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                  ✓ In Stock ({stock} available)
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                  ✗ Out of Stock (Check browser console for debug info)
                </span>
              )}
            </div>

            <h1 className="font-serif text-3xl font-bold text-ink mb-2">{product.name}</h1>
            <p className="text-ink-muted text-sm mb-4">SKU: {product.sku}</p>
            
            {product.description && (
              <p className="text-ink-soft mb-6">{product.description}</p>
            )}

            <div className="text-4xl font-bold text-sage-700 mb-8">
              ${product.price.toFixed(2)}
            </div>

            {/* Quantity Selector */}
            {inStock && (
              <div className="mb-6">
                <label className="block text-sm font-semibold text-ink-soft mb-2">
                  Quantity
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-14 h-14 bg-cream-200 rounded-xl text-2xl font-bold text-ink-soft hover:bg-cream-300 transition-colors active:scale-95"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={stock}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.min(stock, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="w-20 h-14 text-center text-2xl font-bold border-2 border-cream-300 rounded-xl focus:border-sage-500 focus:outline-none"
                  />
                  <button
                    onClick={() => setQuantity(Math.min(stock, quantity + 1))}
                    className="w-14 h-14 bg-cream-200 rounded-xl text-2xl font-bold text-ink-soft hover:bg-cream-300 transition-colors active:scale-95"
                  >
                    +
                  </button>
                </div>
                <p className="text-sm text-ink-muted mt-2">
                  Subtotal: <span className="font-bold">${(product.price * quantity).toFixed(2)}</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-cream-300 shadow-lg">
        <div className="max-w-2xl mx-auto p-4 space-y-3">
          {inStock ? (
            <>
              {/* Stripe Checkout Button (Primary) */}
              {product.stripePriceId ? (
                <>
                  <button
                    onClick={handlePayNow}
                    className="w-full bg-gradient-to-r from-sage-600 to-sage-700 text-white py-4 rounded-xl text-lg font-bold hover:from-sage-800 hover:to-sage-800 transition-all active:scale-98 shadow-lg flex items-center justify-center gap-2"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Pay Now with Stripe ({quantity} {quantity === 1 ? 'item' : 'items'})
                  </button>
                  <p className="text-xs text-center text-ink-muted">Secure checkout • Quantity: {quantity} • ${(product.price * quantity).toFixed(2)}</p>
                </>
              ) : null}
              
              {/* Always show Add to Cart as option */}
              <button
                id="add-to-cart-btn"
                onClick={handleAddToCart}
                className={`w-full py-4 rounded-xl text-lg font-bold transition-all active:scale-98 shadow-lg ${
                  product.stripePriceId 
                    ? 'bg-cream-200 text-ink-soft hover:bg-cream-300 border border-cream-300' 
                    : 'bg-gradient-to-r from-sage-700 to-sage-600 text-white hover:from-sage-800 hover:to-sage-800'
                }`}
              >
                🛒 {product.stripePriceId ? 'Add to Cart Instead' : 'Add to Cart'}
              </button>
              
              {!product.stripePriceId && (
                <p className="text-xs text-center text-amber-600">
                  💡 Tip: Admin can enable instant Stripe checkout by syncing products
                </p>
              )}
              {cartItemCount > 0 && (
                <button
                  onClick={handleCheckout}
                  className="w-full bg-green-600 text-white py-3 rounded-xl text-base font-bold hover:bg-green-700 transition-all active:scale-98"
                >
                  💳 Checkout Cart ({cartItemCount} items · ${cartTotal.toFixed(2)})
                </button>
              )}
            </>
          ) : (
            <button
              onClick={() => navigate('/shop')}
              className="w-full bg-gray-300 text-ink-soft py-4 rounded-xl text-lg font-bold cursor-not-allowed"
              disabled
            >
              Out of Stock - Browse Other Products
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
